// SQLite inline-constraint finder — Slice 21 helper for indexBuilder.
//
// SQLite has no separate constraint catalog: DEFAULT and CHECK clauses live
// inline inside the table's CREATE TABLE source. The Slice-17 fetcher returns
// the full CREATE TABLE text under a synthetic `check-constraint` listing
// keyed by the table name; this finder walks that text for `CHECK (...)` and
// `DEFAULT <expr>` regions and forwards each region to the constraint
// expression scanner. Comments and string literals get skipped via the
// shared `trySkipNonCode` helper so parenthesized clauses inside literals
// don't fool the balanced-paren walker.

import { type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { ParseOptions } from '../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../sql-resolver/classify/tokenSkip';

import type { IdentifierIndex } from '../types';

import { walkConstraintExpression } from './constraintExpressionScanner';
import type { PersistedObjectListing } from './objectBodyFetcher';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_]/;

export function scanSqliteCreateTableConstraints(
  source: string,
  options: ParseOptions,
  listing: PersistedObjectListing,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): void {
  const skipCtx = skipContextFor(options);
  const len = source.length;
  let i = 0;
  while (i < len) {
    const skipped = trySkipNonCode(source, i, len, skipCtx);
    if (skipped >= 0 && skipped > i) { i = skipped; continue; }
    if (!IDENT_START.test(source[i])) { i += 1; continue; }
    let j = i + 1;
    while (j < len && IDENT_CONT.test(source[j])) j++;
    const keyword = source.slice(i, j).toUpperCase();
    if (keyword === 'CHECK') {
      i = consumeCheck(source, j, options, listing, lineIdx, index);
      continue;
    }
    if (keyword === 'DEFAULT') {
      i = consumeDefault(source, j, options, listing, lineIdx, index);
      continue;
    }
    i = j;
  }
}

function consumeCheck(
  source: string,
  afterKeyword: number,
  options: ParseOptions,
  listing: PersistedObjectListing,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): number {
  let k = afterKeyword;
  while (k < source.length && /\s/.test(source[k])) k++;
  if (source[k] !== '(') return afterKeyword;
  const close = matchClosingParen(source, k);
  if (close < 0) return afterKeyword;
  const inner = source.slice(k + 1, close);
  walkConstraintExpression(inner, k + 1, options, listing, 'check', lineIdx, index);
  return close + 1;
}

function consumeDefault(
  source: string,
  afterKeyword: number,
  options: ParseOptions,
  listing: PersistedObjectListing,
  lineIdx: LineIndex,
  index: IdentifierIndex,
): number {
  let k = afterKeyword;
  while (k < source.length && /\s/.test(source[k])) k++;
  if (k >= source.length) return afterKeyword;

  if (source[k] === '(') {
    const close = matchClosingParen(source, k);
    if (close < 0) return afterKeyword;
    const inner = source.slice(k + 1, close);
    walkConstraintExpression(inner, k + 1, options, listing, 'default', lineIdx, index);
    return close + 1;
  }

  if (IDENT_START.test(source[k])) {
    let m = k + 1;
    while (m < source.length && IDENT_CONT.test(source[m])) m++;
    let n = m;
    while (n < source.length && /\s/.test(source[n])) n++;
    if (source[n] === '(') {
      const close = matchClosingParen(source, n);
      if (close >= 0) {
        const slice = source.slice(k, close + 1);
        walkConstraintExpression(slice, k, options, listing, 'default', lineIdx, index);
        return close + 1;
      }
    }
    const slice = source.slice(k, m);
    walkConstraintExpression(slice, k, options, listing, 'default', lineIdx, index);
    return m;
  }

  return afterKeyword;
}

function matchClosingParen(source: string, openIdx: number): number {
  let depth = 1;
  for (let i = openIdx + 1; i < source.length; i++) {
    const c = source[i];
    if (c === '(') depth += 1;
    else if (c === ')') {
      depth -= 1;
      if (depth === 0) return i;
    } else if (c === "'") {
      i = source.indexOf("'", i + 1);
      if (i < 0) return -1;
    }
  }
  return -1;
}
